import {
  Skeleton,
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  InputField,
  Notice,
  Page,
} from '@laura/design-system';
import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import type { AuthBackend, SignInStep } from './auth.js';
import { socialErrorMessage, type SocialProvider, type SocialResult } from './social.js';
import { OrRule, SocialButtons } from './social-icons.js';

/** Plain-language messages for Cognito errors; the error name is all we read. */
export function authErrorMessage(error: unknown): string {
  const name = error instanceof Error ? error.name : '';
  switch (name) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return 'Email or password is not right.';
    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
      return 'That code did not match. Use the newest code from your email or authenticator app.';
    case 'ExpiredCodeException':
      return 'That code has expired. Ask for a new one.';
    case 'InvalidPasswordException':
      return 'Use at least 12 characters, with upper and lower case letters, a number and a symbol.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'PasswordResetRequiredException':
      return 'Your password needs a reset. Choose "Forgot password".';
    default:
      return 'Something went wrong. Try again in a moment.';
  }
}

function Form({ onSubmit, children }: { onSubmit: () => Promise<void>; children: ReactNode }) {
  return (
    <form
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void onSubmit();
      }}
    >
      {children}
    </form>
  );
}

/** QR code and the secret in text, for authenticator apps (TOTP set-up). */
export function TotpCode({ secret, uri }: { secret: string; uri: string }) {
  const [svg, setSvg] = useState('');
  useEffect(() => {
    // Loaded on demand: only TOTP set-up draws this QR code (plan 45 phase 2).
    let live = true;
    import('qrcode')
      .then((QRCode) => QRCode.default.toString(uri, { type: 'svg', margin: 1, width: 180 }))
      .then((drawn) => live && setSvg(drawn))
      .catch(() => live && setSvg(''));
    return () => {
      live = false;
    };
  }, [uri]);
  return (
    <div className="row row-start">
      {svg && (
        <div
          role="img"
          aria-label="QR code for your authenticator app"
          className="qr"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      )}
      <div>
        <p className="help">Can&apos;t scan? Type this key into your app:</p>
        <p className="text-strong num break-all">{secret}</p>
      </div>
    </div>
  );
}

export interface SignInScreenProps {
  product: string;
  backend: AuthBackend;
  onSignedIn: (email: string) => void;
  /**
   * ADR 188: a full-bleed photo behind the sign-in card (the clinic portal's clinic
   * room). Decorative: `src` and an optional `srcSet` of the same picture. Without it
   * the card sits on the warm ground.
   */
  scene?: {
    src: string;
    srcSet?: string;
    /**
     * ADR 189 (Login D): `start` puts the card on the start side, vertically centred,
     * with the photo filling the rest; on phones the photo becomes a banner above
     * the card. `focus` is the photo's object-position on phones.
     */
    placement?: 'centre' | 'start';
    focus?: string;
  };
  /** A line under the wordmark, e.g. what the product is. */
  tagline?: string;
  /** ADR 189: provider buttons (only the configured ones) and how to start one. */
  social?: {
    providers: readonly SocialProvider[];
    start: (provider: SocialProvider) => Promise<void>;
  };
  /** ADR 189: the provider's answer after the redirect back (`/auth/callback`). */
  resume?: SocialResult | { error: unknown; provider?: SocialProvider };
  /** Filled in from the sign-up (`?email=`). */
  initialEmail?: string;
  /** "New to Laura? Create an account". */
  signUpHref?: string;
  /** A first message, e.g. why the person was signed out (ADR 238 idle sign-out). */
  notice?: string;
}

type Mode = 'credentials' | 'forgot' | 'forgotConfirm';

/** Sign-in with every Cognito step our pools use (plan 03 §5.2, §5.3). */
export function SignInScreen({
  product,
  backend,
  onSignedIn,
  scene,
  tagline,
  social,
  resume,
  initialEmail,
  signUpHref,
  notice,
}: SignInScreenProps) {
  const [mode, setMode] = useState<Mode>('credentials');
  const [step, setStep] = useState<SignInStep | null>(null);
  const [email, setEmail] = useState(initialEmail ?? '');
  // ADR 189: a sign-in through Google, Apple or Microsoft in progress.
  const [federated, setFederated] = useState<SocialResult | null>(null);
  const [password, setPassword] = useState('');
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(notice ?? '');

  const run = async (
    work: () => Promise<void>,
    message: (e: unknown) => string = authErrorMessage,
  ) => {
    setBusy(true);
    setError('');
    try {
      await work();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  const advance = (next: SignInStep, address = email) => {
    setValue('');
    if (next.step === 'done') onSignedIn(address.trim().toLowerCase());
    else if (next.step === 'resetPassword') setMode('forgot');
    else setStep(next);
  };

  /** ADR 189: the provider's proof becomes a clinic session (custom auth). */
  const federatedSignIn = (result: SocialResult) =>
    run(
      async () => {
        const signInWith = backend.federatedSignIn;
        if (!signInWith) throw Object.assign(new Error('unsupported'), { name: 'Unsupported' });
        setEmail(result.email);
        setFederated(result);
        setStep(null);
        const next = await signInWith(result.idToken, result.provider, result.email);
        if (next.step === 'done') setFederated(null);
        advance(next, result.email);
      },
      (e) => {
        setFederated(null);
        return socialErrorMessage(e, result.provider);
      },
    );

  const [resumed, setResumed] = useState(false);
  useEffect(() => {
    if (!resume || resumed) return;
    setResumed(true);
    if ('error' in resume) setError(socialErrorMessage(resume.error, resume.provider));
    else void federatedSignIn(resume);
    // Runs once for the redirect that brought the person back.
  }, [resume, resumed]);

  const startSocial = (provider: SocialProvider) =>
    void run(
      async () => {
        if (!social) return;
        await social.start(provider);
      },
      (e) => socialErrorMessage(e, provider),
    );

  let body: ReactNode;
  if (mode === 'forgot')
    body = (
      <Form
        onSubmit={() =>
          run(async () => {
            await backend.forgotPassword(email);
            setPassword('');
            setMode('forgotConfirm');
            setInfo('We sent a code to your email.');
          })
        }
      >
        <InputField
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <div className="row mt-16">
          <Button onClick={() => setMode('credentials')}>Back</Button>
          <Button type="submit" variant="brand" disabled={busy}>
            Send me a code
          </Button>
        </div>
      </Form>
    );
  else if (mode === 'forgotConfirm')
    body = (
      <Form
        onSubmit={() =>
          run(async () => {
            await backend.confirmForgotPassword(email, value, password);
            setMode('credentials');
            setPassword('');
            setValue('');
            setInfo('Password changed. Sign in with the new one.');
          })
        }
      >
        <InputField
          label="Code from the email"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <InputField
          label="New password"
          type="password"
          autoComplete="new-password"
          help="At least 12 characters, with upper and lower case letters, a number and a symbol."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="row mt-16">
          <Button type="submit" variant="brand" disabled={busy}>
            Set new password
          </Button>
        </div>
      </Form>
    );
  else if (step?.step === 'newPassword')
    body = (
      <Form onSubmit={() => run(async () => advance(await backend.confirm(value)))}>
        <p className="sub">This is your first sign-in. Choose your own password.</p>
        <InputField
          label="New password"
          type="password"
          autoComplete="new-password"
          help="At least 12 characters, with upper and lower case letters, a number and a symbol."
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="row mt-16">
          <Button type="submit" variant="brand" disabled={busy}>
            Save password
          </Button>
        </div>
      </Form>
    );
  else if (step?.step === 'totpSetup')
    body = (
      <Form onSubmit={() => run(async () => advance(await backend.confirm(value)))}>
        <p className="sub">
          Two-step sign-in is required. Add {product} to an authenticator app, then type the 6-digit
          code it shows.
        </p>
        <TotpCode secret={step.secret} uri={step.uri} />
        <InputField
          label="6-digit code"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="row mt-16">
          <Button type="submit" variant="brand" disabled={busy}>
            Turn on two-step sign-in
          </Button>
        </div>
      </Form>
    );
  else if (step?.step === 'totpCode') {
    const useEmailCode = backend.useEmailCode;
    body = (
      <Form onSubmit={() => run(async () => advance(await backend.confirm(value)))}>
        <InputField
          label="6-digit code from your authenticator app"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="spread mt-16">
          <Button type="submit" variant="brand" disabled={busy}>
            Sign in
          </Button>
          {step.emailFallback && useEmailCode && (
            <button
              type="button"
              className="link btn-bare"
              disabled={busy}
              onClick={() => void run(async () => advance(await useEmailCode()))}
            >
              Send a code by email instead
            </button>
          )}
        </div>
      </Form>
    );
  } else if (step?.step === 'emailCode')
    body = (
      <Form onSubmit={() => run(async () => advance(await backend.confirm(value)))}>
        <p className="sub" role="status">
          We emailed a 6-digit code to {step.destination ?? 'your email address'}. It works for a
          few minutes.
        </p>
        <InputField
          label="6-digit code from your email"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="spread mt-16">
          <Button type="submit" variant="brand" disabled={busy}>
            Sign in
          </Button>
          <button
            type="button"
            className="link btn-bare"
            disabled={busy}
            onClick={() => {
              // ADR 189: a provider sign-in starts its custom auth again (a new code).
              if (federated) {
                void federatedSignIn(federated).then(() => setInfo('We sent a new code.'));
                return;
              }
              void run(async () => {
                // A new sign-in sends a new code (Cognito has no "resend" for MFA codes).
                setStep(null);
                advance(await backend.signIn(email, password));
                setInfo('We sent a new code.');
              });
            }}
          >
            Send a new code
          </button>
        </div>
      </Form>
    );
  else if (step?.step === 'unsupported')
    body = (
      <Notice kind="warn">
        This sign-in step is not available here ({step.name}). Ask Novations support.
      </Notice>
    );
  else
    body = (
      <Form onSubmit={() => run(async () => advance(await backend.signIn(email, password)))}>
        <InputField
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <InputField
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="spread mt-16">
          <Button variant="brand" type="submit" disabled={busy || !email || !password}>
            Sign in
          </Button>
          <button type="button" className="link btn-bare" onClick={() => setMode('forgot')}>
            Forgot password
          </button>
        </div>
        {social && social.providers.length > 0 && (
          <>
            <OrRule />
            <SocialButtons providers={social.providers} onChoose={startSocial} disabled={busy} />
          </>
        )}
        {signUpHref && (
          <p className="signin-alt">
            New to {product}? <a href={signUpHref}>Create an account</a>
          </p>
        )}
      </Form>
    );

  const start = scene?.placement === 'start';
  return (
    <div className={start ? 'signin-scene signin-scene-start' : 'signin-scene'}>
      {scene && (
        <img
          className="signin-photo"
          src={scene.src}
          {...(scene.srcSet && { srcSet: scene.srcSet, sizes: '100vw' })}
          {...(scene.focus && { style: { '--signin-focus': scene.focus } as CSSProperties })}
          alt=""
          fetchPriority="high"
        />
      )}
      {scene && <span className="signin-veil" aria-hidden="true" />}
      <main className="signin-card">
        <div className="signin-brand">
          <NovationsMark />
          {tagline && <p className="signin-tagline">{tagline}</p>}
        </div>
        <div className="signin-head">
          <h1>Sign in to {product}</h1>
          <p className="sub">Welcome back. Use the email you were invited with.</p>
        </div>
        {info && <Notice kind="ok">{info}</Notice>}
        {error && <Notice kind="warn">{error}</Notice>}
        {body}
      </main>
    </div>
  );
}

/** The Novations AI wordmark (ADR 188): type only, no box behind it. */
export function NovationsMark() {
  return (
    <p className="novations-mark">
      <span className="novations-mark-n" aria-hidden="true">
        N
      </span>
      <span>
        Novations <span className="novations-mark-ai">AI</span>
      </span>
    </p>
  );
}

/**
 * Re-sign-in for sensitive writes (step-up, architecture §14). Signing in again
 * gives a fresh `auth_time`; cancelling leaves the write undone.
 */
export function StepUpDialog({
  open,
  backend,
  email,
  onDone,
}: {
  open: boolean;
  backend: AuthBackend;
  email: string;
  onDone: (ok: boolean) => void;
}) {
  const [address, setAddress] = useState(email);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const [codeHelp, setCodeHelp] = useState('');
  const [error, setError] = useState('');
  useEffect(() => setAddress(email), [email]);

  const reset = () => {
    setPassword('');
    setCode('');
    setNeedCode(false);
    setError('');
  };

  const submit = async () => {
    setError('');
    try {
      if (!needCode) {
        await backend.signOut(false);
        const next = await backend.signIn(address, password);
        if (next.step === 'totpCode' || next.step === 'emailCode') {
          setCodeHelp(
            next.step === 'emailCode'
              ? 'We emailed you a 6-digit code.'
              : 'Use the code in your authenticator app.',
          );
          return setNeedCode(true);
        }
        if (next.step !== 'done') throw new Error('unexpected step');
      } else {
        const next = await backend.confirm(code);
        if (next.step !== 'done') throw new Error('unexpected step');
      }
      reset();
      onDone(true);
    } catch (e) {
      setError(authErrorMessage(e));
    }
  };

  return (
    <Dialog
      open={open}
      title="Confirm it's you"
      subtitle="This change needs a recent sign-in. Sign in again to continue."
      onClose={() => {
        reset();
        onDone(false);
      }}
      footer={
        <>
          <Button
            onClick={() => {
              reset();
              onDone(false);
            }}
          >
            Cancel
          </Button>
          <Button variant="brand" onClick={() => void submit()}>
            {needCode ? 'Confirm' : 'Continue'}
          </Button>
        </>
      }
    >
      {error && <Notice kind="warn">{error}</Notice>}
      {!open ? null : needCode ? (
        <InputField
          label="6-digit code"
          help={codeHelp}
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      ) : (
        <>
          <InputField
            label="Email"
            type="email"
            autoComplete="username"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          <InputField
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </>
      )}
    </Dialog>
  );
}

/**
 * A route that exists now and works later (plan 03 §5.3). The page says what
 * it will do in product words; build terms such as epic numbers never reach a
 * clinic (ADR 126, D16). `epic` is kept for the route tables and shown only
 * when it reads as a product word ("no epic" means a plain not-found page).
 */
export function Placeholder({ title, what, epic }: { title: string; what: string; epic?: string }) {
  const notFound = !epic || epic === 'no epic';
  return (
    <Page title={title}>
      <Card className="placeholder">
        <EmptyState title={notFound ? title : `${title} is on its way`}>{what}</EmptyState>
        {!notFound && (
          <div className="placeholder-note">
            <Badge tone="grey">Not available yet</Badge>
          </div>
        )}
      </Card>
    </Page>
  );
}

export function Loading() {
  return (
    <div className="page" aria-busy="true">
      <span className="sr-only" role="status">
        Loading
      </span>
      <Skeleton kind="text" lines={2} />
      <Skeleton kind="card" />
    </div>
  );
}
